import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { Button } from '@/common/components/Button.tsx'
import Image from '@/common/components/Image/Image.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { MarketplaceStore } from '@/queries/marketplace/schema'
import { MarketplaceProvider } from '@/queries/providers/schema'
import { useTranslation } from 'react-i18next'

type CardItemProps = {
  item: MarketplaceItem
  provider: MarketplaceProvider | MarketplaceStore | MarketplaceItem['provider']
  onItemClick: (item: MarketplaceItem) => void
  onProviderClick?: (providerId: number) => void
}

const CardItem = ({
  item,
  onItemClick,
  onProviderClick,
  provider,
}: CardItemProps) => {
  const { t } = useTranslation()
  const canShop = provider.accepting_new_clients === 1

  const displayName = item.third_party_item?.custom_name ?? item.name
  const displayPrice =
    item.third_party_item?.custom_price &&
    !isNaN(parseFloat(item.third_party_item.custom_price))
      ? parseFloat(item.third_party_item.custom_price)
      : item.price

  return (
    <div key={item.id} className="border rounded-lg shadow-lg p-4 flex gap-4">
      <div className="w-1/3 flex justify-center">
        {item.image ? (
          <Image className="object-contain size-[100px]" path={item.image} />
        ) : (
          <div className="h-[100px] w-[100px] bg-gray-200 rounded-md flex items-center justify-center text-sm text-gray-500">
            No image
          </div>
        )}
      </div>
      <div className="w-2/3 flex flex-col justify-between">
        <h2
          className={cn('text-lg font-semibold leading-tight', {
            'hover:text-primary-600 cursor-pointer': canShop,
          })}
          onClick={() => {
            if (canShop) {
              onItemClick(item)
            }
          }}
        >
          {displayName}
        </h2>
        <p className="text-sm text-primary">{item.item.name}</p>
        <p className="text-gray-500">
          Sold by:{' '}
          {onProviderClick ? (
            <button
              type="button"
              className="font-semibold text-primary-600 hover:underline"
              onClick={() => onProviderClick(item.provider_id)}
            >
              {item.provider.name ?? 'Unknown'}
            </button>
          ) : (
            <span className="font-semibold text-primary-600">
              {item.provider.name ?? 'Unknown'}
            </span>
          )}
        </p>
        <p className="text-xl font-bold text-gray-900 mt-2">
          {currencyFormatter.format(displayPrice)}
        </p>
        <Button
          disabled={!canShop}
          onClick={() => onItemClick(item)}
          className="w-max py-2 px-4 mt-3"
        >
          {t('shop')}
        </Button>
      </div>
    </div>
  )
}
export default CardItem
